import React, { useState, useEffect } from 'react';
import axios from 'axios';

const FramingForm = ({ journeyId, token, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    target_user: '',
    urgency_driver: '',
    desired_outcome: '',
    current_pain: '',
    constraints: '',
    success_metric: '',
    value_proposition: '',
    target_segment: '',
    workflow_steps: '',
    location: '',
    framing_type: 'product',
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [existingFraming, setExistingFraming] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);

  useEffect(() => {
    loadExistingFraming();
  }, [journeyId, token]);

  const loadExistingFraming = async () => {
    try {
      const response = await axios.get(
        `http://localhost:5000/api/journeys/${journeyId}/framing`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.framing) {
        setExistingFraming(response.data.framing);
        setFormData(response.data.framing);
      }
    } catch (err) {
      console.error('Load existing framing error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRadioChange = (e) => {
    setFormData({ ...formData, framing_type: e.target.value });
  };

  const isFormValid = () => {
    return (
      formData.target_user.trim().length >= 10 &&
      formData.urgency_driver.trim().length >= 10 &&
      formData.desired_outcome.trim().length >= 10 &&
      formData.success_metric.trim().length >= 5
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload = {
        target_user: formData.target_user.trim(),
        urgency_driver: formData.urgency_driver.trim(),
        desired_outcome: formData.desired_outcome.trim(),
        current_pain: formData.current_pain.trim() || null,
        constraints: formData.constraints.trim() || null,
        success_metric: formData.success_metric.trim(),
        value_proposition: formData.value_proposition.trim() || null,
        target_segment: formData.target_segment.trim() || null,
        workflow_steps: formData.workflow_steps.trim() || null,
        location: formData.location.trim() || null,
        framing_type: formData.framing_type,
      };

      const response = await axios.post(
        `http://localhost:5000/api/journeys/${journeyId}/framing`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccessMessage('Framing submitted successfully! Ready for ideation.');
      setExistingFraming(response.data.framing);
      setIsEditing(false);
      if (onSuccess) {
        setTimeout(() => onSuccess(response.data.framing), 1500);
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to submit framing. Please try again.';
      setErrorMessage(errorMsg);
      console.error('Framing submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      target_user: '',
      urgency_driver: '',
      desired_outcome: '',
      current_pain: '',
      constraints: '',
      success_metric: '',
      value_proposition: '',
      target_segment: '',
      workflow_steps: '',
      location: '',
      framing_type: 'product',
    });
  };

  if (existingFraming && !isEditing) {
    return (
      <div className="form-card" style={{ backgroundColor: '#f9f9f9', opacity: 0.95 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 className="form-title">Problem Framing</h2>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#d4edda',
              color: '#155724',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '600',
              marginTop: '8px',
            }}>
              ✓ COMPLETE
            </div>
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => setShowConfirmEdit(true)}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            ✏️ Edit
          </button>
        </div>

        {successMessage && <div className="form-success">{successMessage}</div>}
        {errorMessage && <div className="form-error">{errorMessage}</div>}

        {showConfirmEdit && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }} onClick={() => setShowConfirmEdit(false)}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '400px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: '12px', color: '#2c3e50' }}>Edit Completed Framing?</h3>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                This framing has already been marked complete. Changes will be logged in the audit trail.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setShowConfirmEdit(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setIsEditing(true);
                    setShowConfirmEdit(false);
                  }}
                >
                  Continue Editing
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: '20px' }}>
          {formData.target_user && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Target User</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.target_user}</p>
            </div>
          )}
          {formData.urgency_driver && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Urgency Driver</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.urgency_driver}</p>
            </div>
          )}
          {formData.desired_outcome && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Desired Outcome</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.desired_outcome}</p>
            </div>
          )}
          {formData.success_metric && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Success Metric</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.success_metric}</p>
            </div>
          )}
          {formData.current_pain && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Current Pain</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.current_pain}</p>
            </div>
          )}
          {formData.constraints && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Constraints</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.constraints}</p>
            </div>
          )}
          {formData.value_proposition && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Value Proposition</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.value_proposition}</p>
            </div>
          )}
          {formData.target_segment && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Target Segment</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.target_segment}</p>
            </div>
          )}
          {formData.workflow_steps && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Workflow Steps</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.workflow_steps}</p>
            </div>
          )}
          {formData.location && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Location</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.location}</p>
            </div>
          )}
        </div>

        <div className="form-actions">
          {onCancel && <button className="btn btn-secondary" onClick={onCancel}>Back</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="form-card">
      <h2 className="form-title">Problem Framing</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>Step 2 of 5: Frame the Problem</p>

      {successMessage && <div className="form-success">{successMessage}</div>}
      {errorMessage && <div className="form-error">{errorMessage}</div>}

      <form onSubmit={handleSubmit}>
        {/* Framing Type */}
        <div className="form-group">
          <label>Focus Area *</label>
          <div className="radio-group">
            <div className="radio-option">
              <input
                type="radio"
                name="framing_type"
                value="product"
                checked={formData.framing_type === 'product'}
                onChange={handleRadioChange}
              />
              <label>Product</label>
            </div>
            <div className="radio-option">
              <input
                type="radio"
                name="framing_type"
                value="process"
                checked={formData.framing_type === 'process'}
                onChange={handleRadioChange}
              />
              <label>Process</label>
            </div>
            <div className="radio-option">
              <input
                type="radio"
                name="framing_type"
                value="both"
                checked={formData.framing_type === 'both'}
                onChange={handleRadioChange}
              />
              <label>Both</label>
            </div>
          </div>
        </div>

        {/* Target User */}
        <div className="form-group">
          <label>Target User *</label>
          <textarea
            name="target_user"
            value={formData.target_user}
            onChange={handleInputChange}
            placeholder="Who is the primary user or stakeholder? (min 10 chars)"
            maxLength="1000"
          />
          <div className="char-count">
            {formData.target_user.length}/1000 characters
          </div>
        </div>

        {/* Urgency Driver */}
        <div className="form-group">
          <label>Urgency Driver *</label>
          <textarea
            name="urgency_driver"
            value={formData.urgency_driver}
            onChange={handleInputChange}
            placeholder="What makes this urgent? Why now? (min 10 chars)"
            maxLength="1000"
          />
          <div className="char-count">
            {formData.urgency_driver.length}/1000 characters
          </div>
        </div>

        {/* Desired Outcome */}
        <div className="form-group">
          <label>Desired Outcome *</label>
          <textarea
            name="desired_outcome"
            value={formData.desired_outcome}
            onChange={handleInputChange}
            placeholder="What does success look like? (min 10 chars)"
            maxLength="1000"
          />
          <div className="char-count">
            {formData.desired_outcome.length}/1000 characters
          </div>
        </div>

        {/* Success Metric */}
        <div className="form-group">
          <label>Success Metric *</label>
          <textarea
            name="success_metric"
            value={formData.success_metric}
            onChange={handleInputChange}
            placeholder="How will we measure success? (min 5 chars)"
            maxLength="500"
          />
          <div className="char-count">
            {formData.success_metric.length}/500 characters
          </div>
        </div>

        {/* Current Pain */}
        <div className="form-group">
          <label>Current Pain</label>
          <textarea
            name="current_pain"
            value={formData.current_pain}
            onChange={handleInputChange}
            placeholder="What's broken or painful now?"
            maxLength="1000"
          />
          <div className="char-count">
            {formData.current_pain.length}/1000 characters
          </div>
        </div>

        {/* Constraints */}
        <div className="form-group">
          <label>Constraints</label>
          <textarea
            name="constraints"
            value={formData.constraints}
            onChange={handleInputChange}
            placeholder="What are the limitations or constraints?"
            maxLength="1000"
          />
          <div className="char-count">
            {formData.constraints.length}/1000 characters
          </div>
        </div>

        {/* Product-specific fields */}
        {(formData.framing_type === 'product' || formData.framing_type === 'both') && (
          <>
            <div className="form-group">
              <label>Value Proposition</label>
              <textarea
                name="value_proposition"
                value={formData.value_proposition}
                onChange={handleInputChange}
                placeholder="What unique value does this offer?"
                maxLength="1000"
              />
              <div className="char-count">
                {formData.value_proposition.length}/1000 characters
              </div>
            </div>

            <div className="form-group">
              <label>Target Segment</label>
              <textarea
                name="target_segment"
                value={formData.target_segment}
                onChange={handleInputChange}
                placeholder="Which market segment are we targeting?"
                maxLength="1000"
              />
              <div className="char-count">
                {formData.target_segment.length}/1000 characters
              </div>
            </div>
          </>
        )}

        {/* Process-specific fields */}
        {(formData.framing_type === 'process' || formData.framing_type === 'both') && (
          <>
            <div className="form-group">
              <label>Workflow Steps</label>
              <textarea
                name="workflow_steps"
                value={formData.workflow_steps}
                onChange={handleInputChange}
                placeholder="Describe the workflow or process flow"
                maxLength="1000"
              />
              <div className="char-count">
                {formData.workflow_steps.length}/1000 characters
              </div>
            </div>

            <div className="form-group">
              <label>Location</label>
              <textarea
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Where does this process occur?"
                maxLength="500"
              />
              <div className="char-count">
                {formData.location.length}/500 characters
              </div>
            </div>
          </>
        )}

        {/* Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClear}
            disabled={loading}
          >
            Clear
          </button>
          {onCancel && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Back
            </button>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !isFormValid()}
          >
            {loading ? <span className="loading">⏳</span> : 'Submit Framing'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FramingForm;
